import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { createPortal } from 'react-dom';

import { cn } from '../../utils/cn';

import { Badge } from './badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

const MultiSelectContext = React.createContext(undefined);

const useMultiSelect = () => {
  const context = useContext(MultiSelectContext);

  if (!context) {
    throw new Error('useMultiSelect must be used within MultiSelectProvider');
  }

  return context;
};

const MultiSelect = forwardRef(
  (
    {
      value: valueProp,
      onValueChange: onValueChangeProp,
      onDeselect: onDeselectProp,
      onSelect: onSelectProp,
      defaultValue,
      open: openProp,
      onOpenChange,
      defaultOpen,
      onSearch,
      filter,
      disabled,
      maxCount,
      ...popoverProps
    },
    ref,
  ) => {
    const itemCache = useRef(new Map()).current;

    const handleValueChange = useCallback(
      (state) => {
        if (onValueChangeProp) {
          const items = state.map((value) => itemCache.get(value));

          onValueChangeProp(state, items);
        }
      },

      [onValueChangeProp],
    );

    const [value, setValue] = useControllableState({
      prop: valueProp,
      defaultProp: defaultValue,
      onChange: handleValueChange,
    });

    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    });

    const handleSelect = useCallback(
      (value, item) => {
        setValue((prev) => {
          if (prev && prev.includes(value)) {
            return prev;
          }

          onSelectProp?.(value, item);

          return prev ? [...prev, value] : [value];
        });
      },
      [onSelectProp, setValue],
    );

    const handleDeselect = useCallback(
      (value, item) => {
        setValue((prev) => {
          if (!prev || !prev.includes(value)) {
            return prev;
          }

          onDeselectProp?.(value, item);

          return prev.filter((v) => v !== value);
        });
      },
      [onDeselectProp, setValue],
    );

    const contextValue = useMemo(() => {
      return {
        value: value || [],
        open: open || false,
        onSearch,
        filter,
        disabled,
        maxCount,
        onSelect: handleSelect,
        onDeselect: handleDeselect,
        itemCache,
      };
    }, [
      value,
      open,
      onSearch,
      filter,
      disabled,
      maxCount,
      handleSelect,
      handleDeselect,
    ]);

    return (
      <MultiSelectContext.Provider value={contextValue}>
        <PopoverPrimitive.Root
          {...popoverProps}
          open={open}
          onOpenChange={setOpen}
        />
      </MultiSelectContext.Provider>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';

const MultiSelectTrigger = forwardRef(
  ({ className, children, ...props }, forwardedRef) => {
    const { disabled } = useMultiSelect();

    return (
      <PopoverPrimitive.Trigger ref={forwardedRef} asChild>
        <div
          aria-disabled={disabled}
          data-disabled={disabled}
          {...props}
          className={cn(
            'flex h-auto min-h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-input !bg-background  px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring [&>span]:line-clamp-1',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-text',
            className,
          )}
          onClick={disabled ? PreventClick : props.onClick}
          onTouchStart={disabled ? PreventClick : props.onTouchStart}
        >
          {children}
          <ChevronsUpDown
            aria-hidden
            className="ml-2 size-4 shrink-0 opacity-50"
          />
        </div>
      </PopoverPrimitive.Trigger>
    );
  },
);

MultiSelectTrigger.displayName = 'MultiSelectTrigger';

const PreventClick = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const MultiSelectValue = forwardRef(
  (
    { className, placeholder, maxDisplay, maxItemLength, ...props },
    forwardRef,
  ) => {
    const { value, itemCache, onDeselect } = useMultiSelect();
    const [firstRendered, setFirstRendered] = useState(false);

    const renderRemain =
      maxDisplay && value.length > maxDisplay ? value.length - maxDisplay : 0;
    const renderItems = renderRemain ? value.slice(0, maxDisplay) : value;

    useLayoutEffect(() => {
      setFirstRendered(true);
    }, []);

    if (!value.length || !firstRendered) {
      return (
        <span className="pointer-events-none text-muted-foreground">
          {placeholder}
        </span>
      );
    }

    return (
      <TooltipProvider delayDuration={300}>
        <div
          className={cn(
            'flex flex-1 flex-wrap items-center gap-1.5 overflow-x-hidden',
            className,
          )}
          {...props}
          ref={forwardRef}
        >
          {renderItems.map((value) => {
            const item = itemCache.get(value);
            const content = item?.label || value;
            const child =
              maxItemLength &&
              typeof content === 'string' &&
              content.length > maxItemLength
                ? `${content.slice(0, maxItemLength)}...`
                : content;
            const el = (
              <Badge
                variant="outline"
                key={value}
                className="group/multi-select-badge cursor-pointer rounded-full pr-1.5"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeselect(value, item);
                }}
              >
                <span>{child}</span>
                <X className="ml-1 h-3 w-3 text-muted-foreground group-hover/multi-select-badge:text-foreground" />
              </Badge>
            );

            if (child !== content) {
              return (
                <Tooltip key={value}>
                  <TooltipTrigger className="inline-flex">{el}</TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    className="z-[51]"
                  >
                    {content}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return el;
          })}
          {renderRemain ? (
            <span className="py-.5 text-xs leading-4 text-muted-foreground">
              +{renderRemain}
            </span>
          ) : null}
        </div>
      </TooltipProvider>
    );
  },
);

const MultiSelectSearch = forwardRef((props, ref) => {
  const { onSearch } = useMultiSelect();

  return <CommandInput ref={ref} {...props} onValueChange={onSearch} />;
});

MultiSelectSearch.displayName = 'MultiSelectSearch';

const MultiSelectList = forwardRef(({ className, ...props }, ref) => {
  return (
    <CommandList
      ref={ref}
      className={cn('max-h-[unset] px-0 py-1', className)}
      {...props}
    />
  );
});

MultiSelectList.displayName = 'MultiSelectList';

const MultiSelectContent = forwardRef(
  ({ className, children, ...props }, ref) => {
    const context = useMultiSelect();
    const fragmentRef = useRef();

    if (!fragmentRef.current && typeof window !== 'undefined') {
      fragmentRef.current = document.createDocumentFragment();
    }

    if (!context.open) {
      return fragmentRef.current
        ? createPortal(<Command>{children}</Command>, fragmentRef.current)
        : null;
    }

    return (
      <PopoverPrimitive.Portal forceMount>
        <PopoverPrimitive.Content
          ref={ref}
          align="start"
          sideOffset={4}
          collisionPadding={10}
          className={cn(
            'z-50 w-full rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          )}
          style={{
            '--radix-select-content-transform-origin':
              'var(--radix-popper-transform-origin)',
            '--radix-select-content-available-width':
              'var(--radix-popper-available-width)',
            '--radix-select-content-available-height':
              'var(--radix-popper-available-height)',
            '--radix-select-trigger-width': 'var(--radix-popper-anchor-width)',
            '--radix-select-trigger-height':
              'var(--radix-popper-anchor-height)',
          }}
          {...props}
        >
          <Command
            className={cn(
              'max-h-96 w-full min-w-[var(--radix-select-trigger-width)] px-1',
              className,
            )}
            shouldFilter={!context.onSearch}
            filter={(value, search) => {
              if (value.includes(search)) return 1;
              return 0;
            }}
          >
            {children}
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  },
);

const MultiSelectItem = forwardRef(
  (
    {
      value,
      onSelect: onSelectProp,
      onDeselect: onDeselectProp,
      children,
      label,
      disabled: disabledProp,
      className,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      value: contextValue,
      maxCount,
      onSelect,
      onDeselect,
      itemCache,
    } = useMultiSelect();

    const item = useMemo(() => {
      return value
        ? {
            value,
            label:
              label || (typeof children === 'string' ? children : undefined),
          }
        : undefined;
    }, [value, label, children]);

    const selected = Boolean(value && contextValue.includes(value));

    useEffect(() => {
      if (value) {
        itemCache.set(value, item);
      }
    }, [selected, value, item]);

    const disabled = Boolean(
      disabledProp ||
        (!selected && maxCount && contextValue.length >= maxCount),
    );

    const handleClick = () => {
      if (selected) {
        onDeselectProp?.(value, item);
        onDeselect(value, item);
      } else {
        itemCache.set(value, item);
        onSelectProp?.(value, item);
        onSelect(value, item);
      }
    };

    return (
      <CommandItem
        {...props}
        value={value}
        className={cn(
          disabled && 'cursor-not-allowed text-muted-foreground',
          className,
        )}
        disabled={disabled}
        onSelect={!disabled && value ? handleClick : undefined}
        ref={forwardedRef}
      >
        {selected ? (
          <Check className="size-4 shrink-0" />
        ) : (
          <div className="size-4 shrink-0" />
        )}
        <span className="ml-2 overflow-hidden text-ellipsis whitespace-nowrap">
          {children || label || value}
        </span>
      </CommandItem>
    );
  },
);

const MultiSelectGroup = forwardRef((props, forwardRef) => {
  return <CommandGroup {...props} ref={forwardRef} />;
});

MultiSelectGroup.displayName = 'MultiSelectGroup';

const MultiSelectSeparator = forwardRef((props, forwardRef) => {
  return <CommandSeparator {...props} ref={forwardRef} />;
});

MultiSelectSeparator.displayName = 'MultiSelectSeparator';

const MultiSelectEmpty = forwardRef(
  ({ children = 'No Content', ...props }, forwardRef) => {
    return (
      <CommandEmpty {...props} ref={forwardRef}>
        {children}
      </CommandEmpty>
    );
  },
);

MultiSelectEmpty.displayName = 'MultiSelectEmpty';

const renderMultiSelectOptions = (list) => {
  return list.map((option, index) => {
    if ('type' in option) {
      if (option.type === 'separator') {
        return <MultiSelectSeparator key={index} />;
      }

      return null;
    }

    if ('children' in option) {
      return (
        <MultiSelectGroup
          key={option.value || index}
          value={option.value}
          heading={option.heading}
        >
          {renderMultiSelectOptions(option.children)}
        </MultiSelectGroup>
      );
    }

    return (
      <MultiSelectItem key={option.value} {...option}>
        {option.label}
      </MultiSelectItem>
    );
  });
};

export {
  MultiSelect,
  MultiSelectTrigger,
  MultiSelectValue,
  MultiSelectSearch,
  MultiSelectContent,
  MultiSelectList,
  MultiSelectItem,
  MultiSelectGroup,
  MultiSelectSeparator,
  MultiSelectEmpty,
  renderMultiSelectOptions,
};
